// Copyright 2026 The Simlin Authors. All rights reserved.
// Use of this source code is governed by the Apache License,
// Version 2.0, that can be found in the LICENSE file.

// CO-3: the Settings surface, a modal opened from the dashboard menu. It gathers
// the local-first editor preferences (theme, default canvas view, dot-grid, and
// paper tint) in one place. Presentation-only: the current values and change
// handlers come from Home, which owns persistence (theme.ts + preferences.ts).
// The decision/merge logic lives in preferences.ts (unit-tested); this component
// stays thin, matching the WelcomeDialog precedent for app dialogs.

import * as React from 'react';

import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Checkbox, FormControlLabel } from '@simlin/diagram';

import { Theme } from './theme';
import { Preferences, DefaultView, CanvasPaper } from './preferences';

import styles from './SettingsDialog.module.css';

interface SettingsDialogProps {
  open: boolean;
  theme: Theme;
  preferences: Preferences;
  onThemeChange: (theme: Theme) => void;
  onPreferencesChange: (prefs: Preferences) => void;
  onClose: () => void;
}

const VIEW_OPTIONS: ReadonlyArray<{ id: DefaultView; label: string }> = [
  { id: 'build', label: 'Build' },
  { id: 'loops', label: 'Loops' },
  { id: 'play', label: 'Test' },
];

const PAPER_OPTIONS: ReadonlyArray<{ id: CanvasPaper; label: string }> = [
  { id: 'white', label: 'White' },
  { id: 'yellow', label: 'Yellow' },
  { id: 'green', label: 'Green' },
];

/** A small segmented button group for the multi-choice preferences. Kept local
 *  to the dialog -- the shared library has no segmented control, and this is the
 *  only place that needs one on the dashboard. */
function Segmented<T extends string>(props: {
  ariaLabel: string;
  value: T;
  options: ReadonlyArray<{ id: T; label: string }>;
  onSelect: (id: T) => void;
}): React.ReactElement {
  const { ariaLabel, value, options, onSelect } = props;
  return (
    <div className={styles.segmented} role="group" aria-label={ariaLabel}>
      {options.map((o) => {
        const selected = value === o.id;
        return (
          <Button
            key={o.id}
            size="small"
            variant={selected ? 'contained' : 'outlined'}
            color="primary"
            aria-pressed={selected}
            onClick={() => {
              if (!selected) {
                onSelect(o.id);
              }
            }}
          >
            {o.label}
          </Button>
        );
      })}
    </div>
  );
}

export const SettingsDialog: React.FC<SettingsDialogProps> = ({
  open,
  theme,
  preferences,
  onThemeChange,
  onPreferencesChange,
  onClose,
}) => {
  return (
    <Dialog open={open} onClose={onClose} className={styles.dialog}>
      <DialogTitle>Settings</DialogTitle>
      <DialogContent>
        <section className={styles.group}>
          <div className={styles.groupLabel}>Appearance</div>
          <FormControlLabel
            control={
              <Checkbox checked={theme === 'dark'} onChange={(checked) => onThemeChange(checked ? 'dark' : 'light')} />
            }
            label="Dark mode"
          />
        </section>

        <section className={styles.group}>
          <div className={styles.groupLabel}>Default view</div>
          <div className={styles.hint}>Which canvas view a model opens in.</div>
          <Segmented
            ariaLabel="Default view"
            value={preferences.defaultView}
            options={VIEW_OPTIONS}
            onSelect={(defaultView) => onPreferencesChange({ ...preferences, defaultView })}
          />
        </section>

        <section className={styles.group}>
          <div className={styles.groupLabel}>Canvas</div>
          <FormControlLabel
            control={
              <Checkbox
                checked={preferences.canvasGrid}
                onChange={(canvasGrid) => onPreferencesChange({ ...preferences, canvasGrid })}
              />
            }
            label="Show dot grid"
          />
          <div className={styles.hint}>Surface tint</div>
          <Segmented
            ariaLabel="Canvas paper"
            value={preferences.canvasPaper}
            options={PAPER_OPTIONS}
            onSelect={(canvasPaper) => onPreferencesChange({ ...preferences, canvasPaper })}
          />
        </section>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary" variant="contained">
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
};
